import { DateRangeInput, DateRangeObject, FormInputSizeEnum } from '@acceso/design-system';
import { memo, useEffect, useMemo } from 'react';

import { useWindowWidth } from '@/hooks/useWindowWidth';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { createAppTzRangeEdge } from '@/utils/app-tz-date-range';
import { endOfAppDay, startOfAppDay } from '@/utils/spain-time';

export function CalendarComponent() {
  const { width } = useWindowWidth();
  const { gte, lte, setFilters } = useProgramBroadcastFilters();

  const defaultDates = useMemo(
    () => ({
      startDate: startOfAppDay().toISOString(),
      endDate: endOfAppDay().toISOString(),
    }),
    []
  );

  useEffect(() => {
    if (!gte && !lte) {
      setFilters({
        gte: defaultDates.startDate,
        lte: defaultDates.endDate,
      });
    }
  }, [gte, lte, defaultDates.startDate, defaultDates.endDate, setFilters]);

  // The picker only works in the browser's clock: it is shown the Madrid wall
  // clock of the range and hands back the instants the user picked.
  const rangeEdge = useMemo(
    () =>
      createAppTzRangeEdge({
        start: new Date(gte || defaultDates.startDate),
        end: new Date(lte || defaultDates.endDate),
      }),
    [gte, lte, defaultDates.startDate, defaultDates.endDate]
  );

  const handleApplyCalendar = (emitted: DateRangeObject) => {
    const date = rangeEdge.fromEmitted(emitted);
    const nextGte = date.start?.toISOString();
    const nextLte = date.end?.toISOString();
    if (nextGte === gte && nextLte === lte) return;

    // One update for both bounds: two would expose a range mixing the new start with the old
    // end to every request built on it.
    setFilters({
      ...(nextGte !== gte && { gte: nextGte }),
      ...(nextLte !== lte && { lte: nextLte }),
      rangeApplyCount: useProgramBroadcastFilters.getState().rangeApplyCount + 1,
    });
  };

  return (
    <DateRangeInput
      onApply={handleApplyCalendar}
      startRange={rangeEdge.passed?.start}
      endRange={rangeEdge.passed?.end}
      position={width < 640 ? 'right' : 'left'}
      months={width < 830 ? 1 : 2}
      isRanged
      showTimePicker
      hiddenPresetRanges={width < 640}
      className="z-30 w-full min-w-[220px] md:w-auto"
      formatStr="dd/MM/yyyy (HH:mm)"
      size={FormInputSizeEnum.ExtraSmall}
    />
  );
}

export const Calendar = memo(CalendarComponent);
